import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');

  const comic = comics.find((c) => c.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryData = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: categoryData?.name || '漫画', href: `/category/${comic.category}` },
    { label: comic.title, href: '#' },
  ];

  const formatViews = (v: number) => {
    if (v >= 10000) return (v / 10000).toFixed(1) + '万';
    return v.toString();
  };

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="px-4 md:px-6 py-3 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              {breadcrumbs.map((crumb, idx) => (
                <li key={crumb.label} className="flex items-center gap-2">
                  {idx > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
                  {crumb.href !== '#' ? (
                    <Link to={crumb.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground-800 font-medium">{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </nav>

        {/* Comic Info */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden border border-background-300">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 古古漫画网`}
                    title={`${comic.title} - 古古漫画网免费全集下拉式阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading text-xl md:text-2xl text-foreground-900">{comic.title}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <span className="text-sm text-foreground-500">作者: {comic.author}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-accent-600">
                    <i className="ri-star-fill"></i> {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>{formatViews(comic.views)}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-xs bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 mt-4 leading-relaxed">{comic.description}</p>

                <div className="flex gap-3 mt-6">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                  >
                    {comic.chapters[0]?.title || '开始阅读'}
                  </Link>
                  <button className="px-6 py-2.5 bg-background-50 hover:bg-background-200 border border-background-300 text-foreground-700 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1.5"></i>收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters & Info Tabs */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1 bg-background-100 rounded-full px-1 py-1 w-fit mb-6">
              {[
                { key: 'chapters' as const, label: '章节列表' },
                { key: 'info' as const, label: '作品信息' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'chapters' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between p-3 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.date}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-5 bg-background-50 border border-background-200 rounded-lg">
                  <h3 className="font-heading text-base text-foreground-900 mb-3">基本信息</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-500">作品名称</span>
                      <span className="text-foreground-800">{comic.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">作者</span>
                      <span className="text-foreground-800">{comic.author}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">分类</span>
                      <span className="text-foreground-800">{categoryData?.name || comic.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">状态</span>
                      <span className="text-foreground-800">{comic.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">最近更新</span>
                      <span className="text-foreground-800">{comic.updateTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">最新章节</span>
                      <span className="text-foreground-800">{comic.latestChapter}</span>
                    </div>
                  </div>
                </div>
                <div className="p-5 bg-background-50 border border-background-200 rounded-lg">
                  <h3 className="font-heading text-base text-foreground-900 mb-3">数据统计</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-600 font-medium">{comic.rating} / 10</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">阅读量</span>
                      <span className="text-foreground-800">{formatViews(comic.views)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">章节数</span>
                      <span className="text-foreground-800">{comic.chapters.length}+ 话</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-500">标签</span>
                      <span className="text-foreground-800">{comic.tags.join(' · ')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}